import React from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";

import {
  MapPin,
  Briefcase,
  IndianRupee,
  Clock,
  Building2,
  Bookmark,
  BookmarkCheck
} from "lucide-react";

import { formatDistanceToNow } from "date-fns";

export default function JobCard({ job, onApply, onSave, isSaved, isApplied, isActionLoading, onClick, isSelected }) {

  const formatSalary = (min, max) => {
    if (!min && !max) return "Not disclosed";
    if (min && max) return `₹${min} - ${max} LPA`;
    if (min) return `₹${min}+ LPA`;
    return `Up to ₹${max} LPA`;
  };

  const formatExperience = (min, max) => {
    if (!min && !max) return "Any";
    if (min && max) return `${min} - ${max} yrs`;
    if (min) return `${min}+ yrs`;
    return `Up to ${max} yrs`;
  };

  const getWorkModeColor = (mode) => {
    switch (mode) {
      case "remote":
        return "bg-green-100 text-green-700";
      case "hybrid":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  return (
    <Card
      className={`p-5 hover:shadow-lg transition-all duration-300 border group cursor-pointer ${isSelected ? 'border-[#2557a7] ring-1 ring-[#2557a7] bg-blue-50/10' : 'border-gray-100'}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-4 flex-1">
          {/* Logo */}
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center border border-gray-100">
            {job.company_logo ? (
              <img
                src={job.company_logo}
                alt={job.company}
                className="w-10 h-10 object-contain rounded-lg"
              />
            ) : (
              <Building2 className="w-7 h-7 text-gray-400" />
            )}
          </div>

          <div className="flex-1 w-full">
            <h3 className="font-semibold text-gray-900 text-lg group-hover:text-blue-600 transition-colors">
              {job.title}
            </h3>
            <p className="text-gray-600 text-sm mt-0.5">{job.company}</p>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm text-gray-500">
              <div className="flex items-center gap-1.5">
                <Briefcase className="w-4 h-4" />
                <span>{formatExperience(job.experience_min, job.experience_max)}</span>
              </div>

              <div className="flex items-center gap-1.5">
                <IndianRupee className="w-4 h-4" />
                <span>{formatSalary(job.salary_min, job.salary_max)}</span>
              </div>

              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                <span>{job.location}</span>
              </div>
            </div>

            {/* Skills */}
            {job.skills?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {job.skills.slice(0, 4).map((skill, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs"
                  >
                    {skill}
                  </Badge>
                ))}

                {job.skills.length > 4 && (
                  <Badge className="bg-gray-100 text-gray-500 text-xs">
                    +{job.skills.length - 4} more
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Top Header with Match Percentage and Save Button */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs px-2 py-1">
            • {Math.floor(Math.random() * 30 + 65)}% match
          </Badge>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSave(job);
            }}
            className={`p-1 rounded-md transition ${isSaved ? "text-blue-600 bg-blue-50" : "text-gray-400 hover:bg-blue-50"
              }`}
          >
            {isSaved ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Card Footer */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-3">
          {job.work_mode && (
            <Badge className={`${getWorkModeColor(job.work_mode)} text-xs`}>
              {job.work_mode.charAt(0).toUpperCase() + job.work_mode.slice(1)}
            </Badge>
          )}

          {job.posted_date && (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Clock className="w-3.5 h-3.5" />
              {formatDistanceToNow(new Date(job.posted_date), { addSuffix: true })}
            </span>
          )}
        </div>

        <Button
          onClick={(e) => {
            e.stopPropagation();
            onApply(job);
          }}
          disabled={isApplied || isActionLoading}
          size="sm"
          className={`rounded-lg px-5 flex items-center gap-1 ${isApplied ? 'bg-green-100 text-green-700' : 'bg-[#2557a7] hover:bg-[#1e4687] text-white'}`}
        >
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1">
            <path d="M7.49991 0.876892C3.84222 0.876892 0.877075 3.84204 0.877075 7.49972C0.877075 11.1574 3.84222 14.1226 7.49991 14.1226C11.1576 14.1226 14.1227 11.1574 14.1227 7.49972C14.1227 3.84204 11.1576 0.876892 7.49991 0.876892ZM1.82707 7.49972C1.82707 4.36671 4.3669 1.82689 7.49991 1.82689C10.6329 1.82689 13.1727 4.36671 13.1727 7.49972C13.1727 10.6327 10.6329 13.1726 7.49991 13.1726C4.3669 13.1726 1.82707 10.6327 1.82707 7.49972ZM8.24992 4.49999C8.24992 4.08577 7.91413 3.75 7.49992 3.75C7.08571 3.75 6.74992 4.08577 6.74992 4.49999V8.24999C6.74992 8.43103 6.81525 8.60596 6.93297 8.74415L9.18297 11.3831C9.45341 11.7005 9.93006 11.7386 10.2475 11.4682C10.5649 11.1977 10.603 10.7211 10.3326 10.4037L8.24992 7.95995V4.49999Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
          </svg>
          {isActionLoading ? "..." : isApplied ? "Applied" : "Quick Apply"}
        </Button>
      </div>
    </Card>
  );
}
